import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

interface CookiePreferences {
  essential: boolean
  analytics: boolean
  marketing: boolean
  preferences: boolean
}

// Advanced Liquid Toggle Switch Component with Goo Effect
const LiquidToggle: React.FC<{
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}> = ({ checked, onChange, disabled = false }) => {
  const [isActive, setIsActive] = useState(false)

  return (
    <button
      className="liquid-toggle"
      aria-pressed={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      onMouseLeave={() => setIsActive(false)}
      onTouchStart={() => setIsActive(true)}
      onTouchEnd={() => setIsActive(false)}
      style={{
        width: '56px',
        height: '32px',
        borderRadius: '100px',
        border: 'none',
        padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        overflow: 'visible',
        backgroundColor: 'transparent',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
        flexShrink: 0
      }}
    >
      {/* SVG Filter for Goo Effect */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <filter id={`goo-${checked ? 'on' : 'off'}-${Math.random()}`}>
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8"
              result="goo"
            />
            <feComposite in="SourceGraphic" in2="goo" operator="atop" />
          </filter>
        </defs>
      </svg>

      {/* Background Track with Gradient */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '100px',
          background: checked
            ? 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)'
            : 'linear-gradient(135deg, #E0E0E0 0%, #BDBDBD 100%)',
          transition: 'background 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)',
          boxShadow: checked
            ? 'inset 0 1px 3px rgba(0, 0, 0, 0.2), 0 0 8px rgba(74, 144, 226, 0.3)'
            : 'inset 0 1px 3px rgba(0, 0, 0, 0.15)'
        }}
      />

      {/* Liquid Container with Goo Filter */}
      <div
        style={{
          position: 'absolute',
          inset: '4px',
          borderRadius: '100px',
          overflow: 'visible'
        }}
      >
        {/* Main Knob */}
        <div
          style={{
            position: 'absolute',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
            top: '50%',
            left: checked ? 'calc(100% - 24px)' : '0',
            transform: `translateY(-50%) scale(${isActive ? 1.15 : 1})`,
            transition: 'left 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.2s ease',
            boxShadow: `
              0 2px 4px rgba(0, 0, 0, 0.2),
              0 1px 2px rgba(0, 0, 0, 0.1),
              inset 0 -1px 2px rgba(0, 0, 0, 0.1),
              inset 0 1px 2px rgba(255, 255, 255, 0.8)
            `,
            willChange: 'left, transform'
          }}
        />

        {/* Liquid Blob Effect */}
        <div
          style={{
            position: 'absolute',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F5F5 100%)',
            top: '50%',
            left: checked ? 'calc(100% - 22px)' : '2px',
            transform: 'translateY(-50%)',
            transition: 'left 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
            opacity: isActive ? 0.8 : 0,
            willChange: 'left, opacity'
          }}
        />
      </div>

      {/* Glow Effect when Active */}
      {checked && (
        <div
          style={{
            position: 'absolute',
            inset: '-3px',
            borderRadius: '100px',
            background: 'radial-gradient(circle, rgba(74, 144, 226, 0.4) 0%, transparent 70%)',
            filter: 'blur(6px)',
            opacity: 0.6,
            zIndex: -1,
            pointerEvents: 'none'
          }}
        />
      )}
    </button>
  )
}

export const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [cookiePrefs, setCookiePrefs] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
    preferences: false
  })

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent')
    if (!consent) {
      setTimeout(() => setShowBanner(true), 1000)
    } else {
      try {
        const saved = JSON.parse(consent)
        setCookiePrefs(saved)
        applyCookiePreferences(saved)
      } catch (e) {
        console.error('Failed to parse cookie preferences')
      }
    }
  }, [])

  const applyCookiePreferences = (prefs: CookiePreferences) => {
    if (prefs.analytics) {
      console.log('Analytics cookies enabled')
    }
    if (prefs.marketing) {
      console.log('Marketing cookies enabled')
    }
    if (prefs.preferences) {
      console.log('Preference cookies enabled')
    }
  }

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookie-consent', JSON.stringify(prefs))
    localStorage.setItem('cookie-consent-date', new Date().toISOString())
    applyCookiePreferences(prefs)
    setShowBanner(false)
    setShowSettings(false)
  }

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true
    }
    setCookiePrefs(allAccepted)
    savePreferences(allAccepted)
  }

  const acceptEssentialOnly = () => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false
    }
    setCookiePrefs(essentialOnly)
    savePreferences(essentialOnly)
  }

  const saveCustomPreferences = () => {
    savePreferences(cookiePrefs)
  }

  if (!showBanner) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed z-50"
        style={{
          bottom: window.innerWidth < 640 ? '16px' : '24px',
          right: window.innerWidth < 640 ? '16px' : '24px',
          left: window.innerWidth < 640 ? '16px' : 'auto'
        }}
        initial={{ y: 100, opacity: 0, scale: 0.9 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 100, opacity: 0, scale: 0.9 }}
        transition={{ 
          duration: 0.5, 
          ease: [0.34, 1.56, 0.64, 1],
          type: 'spring',
          stiffness: 100
        }}
      >
        <div
          className="rounded-3xl shadow-2xl"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.12),
              0 2px 8px rgba(0, 0, 0, 0.08),
              inset 0 1px 0 rgba(255, 255, 255, 0.5),
              inset 0 -1px 0 rgba(0, 0, 0, 0.05)
            `,
            width: window.innerWidth < 640 ? '100%' : (showSettings ? '400px' : '360px'),
            maxWidth: window.innerWidth < 640 ? '100%' : 'calc(100vw - 48px)',
            transition: 'width 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)'
          }}
        >
          {!showSettings ? (
            // Compact Banner
            <div className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <div 
                  className="flex-shrink-0 w-10 h-10 rounded-2xl flex items-center justify-center" 
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(74, 144, 226, 0.15) 0%, rgba(74, 144, 226, 0.05) 100%)',
                    border: '1px solid rgba(74, 144, 226, 0.2)'
                  }}
                >
                  <span style={{ fontSize: '18px' }}>🍪</span>
                </div>
                <div className="flex-1">
                  <h3
                    className="text-base mb-1.5"
                    style={{
                      color: '#1A1A1A',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontWeight: 600,
                      letterSpacing: '-0.01em'
                    }}
                  >
                    Cookie Preferences
                  </h3>
                  <p
                    className="mb-0"
                    style={{
                      color: '#666666',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      fontSize: '13px',
                      lineHeight: '1.5'
                    }}
                  >
                    We use cookies to enhance your experience.{' '}
                    <Link
                      to="/cookies"
                      style={{
                        color: '#4A90E2',
                        textDecoration: 'none',
                        fontWeight: 500
                      }}
                    >
                      Learn more
                    </Link>
                  </p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                <button
                  onClick={() => setShowSettings(true)}
                  className="px-3.5 py-2.5 rounded-xl transition-all duration-300 text-sm flex-1 min-w-[80px]"
                  style={{
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    color: '#1A1A1A',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 1)'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.08)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  Settings
                </button>
                <button
                  onClick={acceptEssentialOnly}
                  className="px-3.5 py-2.5 rounded-xl transition-all duration-300 text-sm flex-1 min-w-[80px]"
                  style={{
                    background: 'rgba(255, 255, 255, 0.8)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    color: '#1A1A1A',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 1)'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.08)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  Essential
                </button>
                <button
                  onClick={acceptAll}
                  className="px-3.5 py-2.5 rounded-xl transition-all duration-300 text-sm flex-1 min-w-[80px]"
                  style={{
                    background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                    color: '#FFFFFF',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(74, 144, 226, 0.35), 0 1px 3px rgba(74, 144, 226, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #3A7BC8 0%, #2A6AAD 100%)'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(74, 144, 226, 0.4), 0 2px 4px rgba(74, 144, 226, 0.25)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(74, 144, 226, 0.35), 0 1px 3px rgba(74, 144, 226, 0.2)'
                  }}
                >
                  Accept All
                </button>
              </div>
            </div>
          ) : (
            // Detailed Settings
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-base"
                  style={{
                    color: '#1A1A1A',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontWeight: 600,
                    letterSpacing: '-0.01em'
                  }}
                >
                  Cookie Settings
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200"
                  style={{
                    background: 'rgba(0, 0, 0, 0.05)',
                    color: '#666666',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 400
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)'
                  }}
                >
                  ×
                </button>
              </div>
              <div className="space-y-3 mb-5">
                <CookieCategory
                  title="Essential"
                  description="Required for the website to function"
                  enabled={cookiePrefs.essential}
                  disabled={true}
                  onChange={() => {}}
                />
                <CookieCategory
                  title="Analytics"
                  description="Help us understand visitor interactions"
                  enabled={cookiePrefs.analytics}
                  onChange={(checked) =>
                    setCookiePrefs({ ...cookiePrefs, analytics: checked })
                  }
                />
                <CookieCategory
                  title="Marketing"
                  description="Track visitors for relevant ads"
                  enabled={cookiePrefs.marketing}
                  onChange={(checked) =>
                    setCookiePrefs({ ...cookiePrefs, marketing: checked })
                  }
                />
                <CookieCategory
                  title="Preferences"
                  description="Remember your settings"
                  enabled={cookiePrefs.preferences}
                  onChange={(checked) =>
                    setCookiePrefs({ ...cookiePrefs, preferences: checked })
                  }
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={saveCustomPreferences}
                  className="px-5 py-2.5 rounded-xl transition-all duration-300 text-sm"
                  style={{
                    background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)',
                    color: '#FFFFFF',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(74, 144, 226, 0.35)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #3A7BC8 0%, #2A6AAD 100%)'
                    e.currentTarget.style.transform = 'translateY(-1px)'
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(74, 144, 226, 0.4)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(74, 144, 226, 0.35)'
                  }}
                >
                  Save Preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

const CookieCategory: React.FC<{
  title: string
  description: string
  enabled: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}> = ({ title, description, enabled, disabled = false, onChange }) => {
  return (
    <div
      className="p-3 rounded-xl flex items-center justify-between gap-4"
      style={{
        background: 'rgba(255, 255, 255, 0.6)',
        border: '1px solid rgba(255, 255, 255, 0.4)',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)'
      }}
    >
      <div className="flex-1 min-w-0" style={{ paddingRight: '8px' }}>
        <h4
          className="text-sm mb-0.5"
          style={{
            color: '#1A1A1A',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontWeight: 600,
            letterSpacing: '-0.01em'
          }}
        >
          {title}
        </h4>
        <p
          style={{
            color: '#666666',
            fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
            fontSize: '11px',
            lineHeight: '1.4',
            margin: 0
          }}
        >
          {description}
        </p>
      </div>
      <div style={{ flexShrink: 0 }}>
        <LiquidToggle
          checked={enabled}
          onChange={onChange}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

export default CookieConsent
